import React, { useState, useMemo, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { Autocomplete, TextField, Chip, CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { MecordColors } from "Constants/Color";
import {
  autocompleteDropdownStyles,
  getDisabledInputStyles,
  filterPopoverStyles,
  tableStyles,
} from "Constants/Style";
import { fetchProductsByDropDown } from "slices/Masters/product/thunk";
import { fetchProducts } from "slices/Masters/product/thunk";
import ServiceAccessoryRow, {
  AccessoryItem,
  createAccessoryFromProduct,
  mapApiAccessoryToItem,
  normalizeAccessoryProduct,
  recalcAccessory,
} from "./ServiceAccessoryRow";

interface AddServiceAccessoriesModalProps {
  show: boolean;
  onHide: () => void;
  serviceName: string;
  serviceCategory: string;
  gstOptions: any[];
  existingAccessories?: AccessoryItem[];
  onAddAccessories: (accessories: AccessoryItem[]) => void;
  currencySymbol?: string;
  isCompositional?: boolean;
  isIntraState?: boolean;
}

const AddServiceAccessoriesModal: React.FC<AddServiceAccessoriesModalProps> = ({
  show,
  onHide,
  serviceName,
  serviceCategory,
  gstOptions,
  existingAccessories = [],
  onAddAccessories,
  currencySymbol = "₹",
  isCompositional = false,
  isIntraState = true,
}) => {
  const dispatch = useDispatch<any>();
  const [accessories, setAccessories] = useState<AccessoryItem[]>([]);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, Record<string, string>>
  >({});

  useEffect(() => {
    if (show) {
      const mappedAccessories = existingAccessories.map((acc) =>
        mapApiAccessoryToItem(
          isCompositional ? { ...acc, gst_percentage: 0 } : acc,
          isCompositional,
          isIntraState
        )
      );
      setAccessories(mappedAccessories);
      setValidationErrors({});
    }
  }, [show, existingAccessories, isCompositional, isIntraState]);

  useEffect(() => {
    if (!show) return;

    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        // Prefer dropdown API; fall back to list API if needed
        let rows: any[] = [];
        try {
          const dropdownResult = await dispatch(fetchProductsByDropDown()).unwrap();
          rows = Array.isArray(dropdownResult?.data)
            ? dropdownResult.data
            : Array.isArray(dropdownResult)
              ? dropdownResult
              : [];
        } catch {
          rows = [];
        }

        if (rows.length === 0) {
          const listResult = await dispatch(
            fetchProducts({
              page: 1,
              pageSize: 500,
              search: "",
              sort: "",
              order: "",
              filters: [],
            })
          ).unwrap();
          rows = Array.isArray(listResult?.data) ? listResult.data : [];
        }

        setProductOptions(
          rows
            .filter((p: any) => p?.product_id && !p?.is_deleted)
            .map(normalizeAccessoryProduct)
            .filter(
              (p: any) =>
                p.category_name?.toLowerCase() === "accessories" &&
                p.sub_category_name?.toLowerCase() === "accessories"
            )
        );
      } catch (error) {
        console.error("Failed to fetch accessory products:", error);
        setProductOptions([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, [show, dispatch]);

  const selectedProductIds = useMemo(
    () => new Set(accessories.map((a) => a.product_id)),
    [accessories]
  );

  const handleProductSelect = (_: any, selectedProducts: any[]) => {
    const currentIds = new Set(accessories.map((a) => a.product_id));
    const newProducts = selectedProducts.filter(
      (p) => !currentIds.has(p.product_id)
    );

    const selectedIds = new Set(selectedProducts.map((p) => p.product_id));
    const remainingAccessories = accessories.filter((a) =>
      selectedIds.has(a.product_id)
    );

    const newAccessories = newProducts.map((p) => {
      const acc = createAccessoryFromProduct(p, isCompositional, isIntraState);
      if (isCompositional) {
        acc.gst_percentage = 0;
        return recalcAccessory(acc, isCompositional, isIntraState);
      }
      return acc;
    });
    setAccessories([...remainingAccessories, ...newAccessories]);

    const updatedErrors = { ...validationErrors };
    Object.keys(updatedErrors).forEach((key) => {
      if (
        !remainingAccessories.some((a) => a.temp_id === key) &&
        !newAccessories.some((a) => a.temp_id === key)
      ) {
        delete updatedErrors[key];
      }
    });
    setValidationErrors(updatedErrors);
  };

  const handleAccessoryChange = (index: number, updated: AccessoryItem) => {
    const newList = [...accessories];
    newList[index] = updated;
    setAccessories(newList);

    if (validationErrors[updated.temp_id]) {
      const updatedErrors = { ...validationErrors };
      delete updatedErrors[updated.temp_id];
      setValidationErrors(updatedErrors);
    }
  };

  const handleRemoveAccessory = (index: number) => {
    const removedId = accessories[index].temp_id;
    const newList = accessories.filter((_, i) => i !== index);
    setAccessories(newList);

    if (validationErrors[removedId]) {
      const updatedErrors = { ...validationErrors };
      delete updatedErrors[removedId];
      setValidationErrors(updatedErrors);
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, Record<string, string>> = {};
    let isValid = true;

    accessories.forEach((acc) => {
      const itemErrors: Record<string, string> = {};

      if (!acc.quantity || acc.quantity <= 0) {
        itemErrors.quantity = "Qty must be > 0";
        isValid = false;
      }

      if (acc.unit_price < 0) {
        itemErrors.unit_price = "Rate cannot be negative";
        isValid = false;
      }

      if (Object.keys(itemErrors).length > 0) {
        errors[acc.temp_id] = itemErrors;
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  const handleAddAccessories = () => {
    if (accessories.length === 0) return;
    if (!validate()) return;
    onAddAccessories(accessories);
    onHide();
  };

  const selectedProducts = useMemo(() => {
    const fromOptions = productOptions.filter((p) =>
      selectedProductIds.has(p.product_id)
    );
    // Keep already-selected accessories even if product list hasn't loaded yet
    const missing = accessories
      .filter((a) => !fromOptions.some((p) => p.product_id === a.product_id))
      .map((a) => ({
        product_id: a.product_id,
        product_name: a.product_name,
        product_sku: a.product_sku,
        category_name: a.category_name,
        sub_category_name: a.sub_category_name,
        make_name: a.make_name,
        model_name: a.model_name,
        unit_name: a.unit_name,
        hsn_code: a.hsn_code,
        unit_price: a.unit_price,
        gst_percentage: a.gst_percentage,
      }));
    return [...fromOptions, ...missing];
  }, [productOptions, selectedProductIds, accessories]);

  const serviceLabel = serviceName || serviceCategory || "Service";

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="accessories-wide-modal"
      backdrop="static"
      keyboard={false}
    >
      <style>
        {`
          .accessories-wide-modal {
            max-width: 80% !important;
            width: 80% !important;
          }
          .accessories-wide-modal .modal-content {
            min-height: 82vh;
          }
          .accessories-wide-modal .modal-body {
            min-height: 64vh;
            max-height: 72vh;
            overflow-y: auto;
            overflow-x: visible;
          }
          .accessories-select-popper {
            z-index: 2000 !important;
          }
          .accessories-select-popper .MuiPaper-root {
            width: 100% !important;
            max-width: 100% !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
          }
          .accessories-select-popper .MuiAutocomplete-listbox {
            overflow-x: hidden !important;
            box-sizing: border-box !important;
          }
          .accessories-select-popper .MuiAutocomplete-option {
            white-space: normal !important;
            overflow: hidden !important;
            height: auto !important;
            min-height: 36px !important;
            max-width: 100% !important;
            align-items: flex-start !important;
            display: flex !important;
            box-sizing: border-box !important;
          }
          .accessories-select-popper .MuiAutocomplete-option > * {
            white-space: normal !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            min-width: 0 !important;
            max-width: 100% !important;
            flex: 1 1 auto !important;
          }
        `}
      </style>
      <Modal.Header
        closeButton
        style={{
          fontFamily: tableStyles.header.fontFamily,
          fontSize: tableStyles.header.fontSize,
          borderBottom: "1px solid #dee2e6",
        }}
      >
        <Modal.Title
          style={{
            fontFamily: tableStyles.header.fontFamily,
            fontSize: "15px",
            fontWeight: tableStyles.header.fontWeight,
          }}
        >
          <div>Add / Edit Accessories</div>
          <div
            style={{
              fontSize: "12px",
              fontWeight: 400,
              color: MecordColors.DefaultText,
              marginTop: "2px",
            }}
          >
            Service: {serviceLabel}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          fontFamily: filterPopoverStyles.fontFamily,
          fontSize: filterPopoverStyles.fontSize,
        }}
      >
        <Form.Group className="mb-3">
          <Form.Label
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            Select Accessories <span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            multiple
            loading={loadingProducts}
            options={productOptions}
            getOptionLabel={(option: any) =>
              `${option.product_name}${option.product_sku ? ` (${option.product_sku})` : ""}`
            }
            value={selectedProducts}
            onChange={handleProductSelect}
            isOptionEqualToValue={(option: any, value: any) =>
              option.product_id === value.product_id
            }
            disableCloseOnSelect
            slotProps={{
              popper: {
                className: "accessories-select-popper",
                sx: { zIndex: 2000 },
                modifiers: [
                  {
                    name: "sameWidth",
                    enabled: true,
                    phase: "beforeWrite",
                    requires: ["computeStyles"],
                    fn: ({ state }: any) => {
                      const width = `${state.rects.reference.width}px`;
                      state.styles.popper.width = width;
                      state.styles.popper.maxWidth = width;
                    },
                  },
                ],
              },
              paper: {
                sx: {
                  width: "100%",
                  maxWidth: "100%",
                  overflow: "hidden",
                },
              },
            }}
            ListboxProps={{
              ...autocompleteDropdownStyles.ListboxProps,
              style: { maxHeight: 320 },
              sx: {
                ...autocompleteDropdownStyles.ListboxProps.sx,
                overflowX: "hidden",
                "& .MuiAutocomplete-option": {
                  ...(autocompleteDropdownStyles.ListboxProps.sx as any)[
                    "& .MuiAutocomplete-option"
                  ],
                  whiteSpace: "normal",
                  overflow: "hidden",
                  alignItems: "flex-start",
                  lineHeight: 1.4,
                  height: "auto",
                  maxWidth: "100%",
                },
              },
            }}
            renderTags={(value, getTagProps) =>
              value.map((option: any, index: number) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.product_id}
                  label={option.product_name}
                  size="small"
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "12px",
                    backgroundColor: MecordColors.ActiveBackground,
                    color: MecordColors.ActiveText,
                    borderColor: MecordColors.ActiveText,
                    height: "auto",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    },
                    "& .MuiChip-deleteIcon": {
                      color: MecordColors.ActiveText,
                      "&:hover": {
                        color: MecordColors.ThemeBGColor,
                      },
                    },
                  }}
                  variant="outlined"
                />
              ))
            }
            renderOption={(props, option) => {
              const { key, style, ...rest } = props as any;
              return (
                <li
                  key={key}
                  {...rest}
                  style={{
                    ...(style || {}),
                    whiteSpace: "normal",
                    overflow: "hidden",
                    height: "auto",
                    minHeight: 36,
                    maxWidth: "100%",
                    alignItems: "flex-start",
                    display: "flex",
                    boxSizing: "border-box",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "13px",
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      display: "block",
                      width: "100%",
                      minWidth: 0,
                      lineHeight: 1.4,
                    }}
                  >
                    {option.product_name}{" "}
                    <span style={{ color: MecordColors.DefaultText }}>
                      ({option.product_sku})
                    </span>
                  </span>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  selectedProducts.length === 0
                    ? loadingProducts
                      ? "Loading products..."
                      : "Search and select accessories..."
                    : ""
                }
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingProducts ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  ...getDisabledInputStyles(false),
                }}
              />
            )}
          />
        </Form.Group>

        {accessories.length > 0 && (
          <div>
            <div
              className="d-flex justify-content-between align-items-center mb-2"
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              <span>Selected Accessories ({accessories.length})</span>
            </div>

            {accessories.map((acc, idx) => (
              <ServiceAccessoryRow
                key={acc.temp_id}
                accessory={acc}
                index={idx}
                gstOptions={gstOptions}
                onChange={(updated) => handleAccessoryChange(idx, updated)}
                onRemove={() => handleRemoveAccessory(idx)}
                errors={validationErrors[acc.temp_id] || {}}
                currencySymbol={currencySymbol}
                isCompositional={isCompositional}
                isIntraState={isIntraState}
              />
            ))}
          </div>
        )}

        {accessories.length === 0 && (
          <div
            className="text-center text-muted py-4"
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
            }}
          >
            {loadingProducts
              ? "Loading products..."
              : "Search and select accessories from the dropdown above."}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-center">
        <Button
          variant="secondary"
          className="px-3 rounded"
          style={{
            fontFamily: filterPopoverStyles.button.fontFamily,
            fontSize: filterPopoverStyles.button.fontSize,
            borderColor: filterPopoverStyles.cancelButton.borderColor,
            color: filterPopoverStyles.cancelButton.color,
            backgroundColor: "#fff",
          }}
          onClick={onHide}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          className="px-3 rounded"
          style={{
            fontFamily: filterPopoverStyles.button.fontFamily,
            fontSize: filterPopoverStyles.button.fontSize,
            backgroundColor: filterPopoverStyles.applyButton.backgroundColor,
            borderColor: filterPopoverStyles.applyButton.backgroundColor,
            color: "#fff",
          }}
          onClick={handleAddAccessories}
          disabled={accessories.length === 0}
        >
          Save Accessories
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddServiceAccessoriesModal;
